import React, { useEffect, useState } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
} from "@heroicons/react/20/solid";
import { Menu, Transition } from "@headlessui/react";
import moment from "moment";
import ShowInfoAfspraak from "./ShowInfoAfspraak";
import {
  classNames,
  generateDaysArrayForMonth,
} from "./calenderUtilties";
import CrudEventCalendly from "./CrudEventCalendly";
import CrudEventCalendlyPatienten from "./CrudEventCalendlyPatienten";
import { useDispatch } from "react-redux";
import { setPagination } from "../../redux/appointmentSlice";


// Title array for days of the week
const titleDayArray = [
  "Maandag",
  "Dinsdag",
  "Woensdag",
  "Donderdag",
  "Vrijdag",
  "Zaterdag",
  "Zondag",
];

const Header = ({ currentDate, onPrevious, onNext, setOpen }) => (
  <header className="flex items-center justify-between border-b border-wa-border bg-wa-panel px-4 py-3 sm:px-6 lg:flex-none">
    <div className="flex min-w-0 items-center gap-2 sm:gap-3">
      <button
        onClick={onPrevious}
        className="-m-1.5 flex p-1.5 text-wa-muted transition hover:text-wa-text"
      >
        <span className="sr-only">Previous month</span>
        <ChevronLeftIcon className="h-7 w-7" aria-hidden="true" />
      </button>
      <h1 className="text-lg font-semibold leading-6 text-wa-text">
        <time>{moment(currentDate).locale("nl-be").format("MMMM YYYY")}</time>
      </h1>
      <button
        onClick={onNext}
        className="-m-1.5 flex p-1.5 text-wa-muted transition hover:text-wa-text"
      >
        <span className="sr-only">Next month</span>
        <ChevronRightIcon className="h-7 w-7" aria-hidden="true" />
      </button>
    </div>
    <ButtonGroup setOpen={setOpen} />
  </header>
);

const ButtonGroup = ({ setOpen }) => (
  <div className="flex items-center">
    <button
      onClick={() => setOpen(true)}
      className="ml-4 rounded-md bg-wa-accent px-3 py-2 text-sm font-semibold text-wa-onaccent shadow-sm transition hover:bg-wa-accentdeep"
    >
      Afspraak maken
    </button>
    {/* Add Menu for additional actions */}
    <Menu as="div" className="relative ml-3 sm:ml-6">
      <Menu.Button className="-mx-2 flex items-center p-2 text-wa-muted transition hover:text-wa-text">
        <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
      </Menu.Button>
      <Transition as={React.Fragment}>
        <Menu.Items className="absolute right-0 z-10 mt-3 w-36 rounded-md border border-wa-border bg-wa-panel2 shadow-lg">
          {/* Add Menu Items here for "Day View", "Week View", etc. */}
        </Menu.Items>
      </Transition>
    </Menu>
  </div>
);

const DaysGrid = ({ days, showAfspraak }) => (
  <div className="flex bg-wa-border text-xs lg:flex-auto">
    <div className=" w-full sm:grid sm:grid-cols-7 sm:grid-rows-5 lg:gap-px">
      {days.map((day) => (
        <Day key={day.date} day={day} showAfspraak={showAfspraak} />
      ))}
    </div>
  </div>
);

const Day = ({ day, showAfspraak }) => (
  <div
    className={classNames(
      day.isCurrentMonth
        ? "bg-wa-panel2 text-wa-text"
        : "bg-slate-500 text-wa-text/80",
      "relative px-3 py-2 sm:h-28 h-32"
    )}
  >
    <time
      dateTime={day.date}
      className={
        day.isToday
          ? "flex h-6 w-6 items-center justify-center rounded-full bg-wa-accent font-semibold text-wa-onaccent"
          : undefined
      }
    >
      {day.date.split("-").pop().replace(/^0/, "")}
    </time>
    {day?.events?.length > 0 && (
      <ol className="mt-2">
        {day?.events.slice(0, 3).map((event) => (
          <li
            key={event.id}
            title={event?.admin_accept == 0 ? "Moet bevestigd worden" : ""}
            className={classNames(
              "mt-1 px-1 rounded cursor-pointer",
              "text-white",
              event.color
            )}
            onClick={() => showAfspraak(event)}
          >
            <a className="group flex">
              <p
                className={classNames(
                  "text-white",
                  "truncate font-medium"
                )}
              >
                {event?.name} 
              </p>
              <time
                dateTime={event?.start_time}
                className={classNames(
                  "text-white",
                  "ml-3 hidden xl:block"
                )}
              >
                {moment(event.start_time).format("HH:mm")}
              </time>
            </a>
          </li>
        ))}
        {day?.events?.length > 3 && (
          <li className="text-wa-muted">+ {day?.events?.length - 3} more</li>
        )}
      </ol>
    )}
  </div>
);

const MonthView = ({ user, userdetails, select, appointments }) => {
  const [currentDate, setCurrentDate] = useState(
    moment().locale("nl-be").format("YYYY-MM")
  );
  const [days, setDays] = useState(
    generateDaysArrayForMonth(currentDate, null, appointments)
  );

  const dispatch = useDispatch();



  const [open, setOpen] = useState(false);
  const [afspraak, setAfspraak] = useState(null);
  const [openModalEdit, setOpenModalEdit] = useState(false);

  const showAfspraak = (afspr) => {
    setAfspraak(afspr);
    setOpenModalEdit(true);
  };

  useEffect(() => {
    setDays(generateDaysArrayForMonth(currentDate, null, appointments));
  }, [currentDate, appointments]);

  const handlePreviousMonth = () =>{
    setCurrentDate(moment(currentDate).subtract(1, "month").format("YYYY-MM"));
    dispatch(setPagination(moment(currentDate).subtract(1, "month").startOf('month').toISOString()));
  };
  
  const handleNextMonth = () => {
    setCurrentDate(moment(currentDate).add(1, "month").format("YYYY-MM"));
    dispatch(setPagination(moment(currentDate).add(1, "month").startOf('month').toISOString()));
  }

  return (
    <div className="rounded-lg border border-wa-border bg-wa-panel lg:flex lg:flex-col">
      <Header
        currentDate={currentDate}
        onPrevious={handlePreviousMonth}
        onNext={handleNextMonth}
        setOpen={setOpen}
      />
      <div className="shadow ring-1 ring-wa-border lg:flex lg:flex-auto lg:flex-col">
        <div className="grid grid-cols-7 gap-px border-b border-wa-border bg-wa-border text-center text-xs font-semibold">
          {titleDayArray.map((title, key) => (
            <div
              key={key}
              className="bg-wa-panel py-2 text-wa-text"
            >
              {title}
            </div>
          ))}
        </div>
        <DaysGrid days={days} showAfspraak={showAfspraak} />
      </div>
      {user?.role == 2 ? (
        <CrudEventCalendly openModel={open} setOpenModel={setOpen} />
      ) : (
        <CrudEventCalendlyPatienten openModel={open} setOpenModel={setOpen} />
      )}

      {afspraak && (
        <ShowInfoAfspraak
          open={openModalEdit}
          setOpen={setOpenModalEdit}
          select={select}
          evenment={afspraak}
          user={user}
        />
      )}
    </div>
  );
};

export default MonthView;
